import React, { forwardRef, useCallback, useMemo, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const QuantityInput = forwardRef((props, ref) => {
    const {
        id,
        label,
        className,
        placeholder,
        value = "",
        onChange,
        error = "",
        required = false,
        errorTextPlaceholder = {},
        hint,
        hintClassName = "text-muted",
        disabled = false,
        onBlur,
        onFocus,
        suffix,
        decimalPlaces = 3,
        ...rest
    } = props;

    const { t } = useTranslation();
    const [focused, setFocused] = useState(false);
    const [editingValue, setEditingValue] = useState("");

    const fieldId = useMemo(
        () => id || `quantity-input-${Math.random().toString(36).substr(2, 9)}`,
        [id],
    );

    const formatQuantity = useCallback((num) => {
        if (!num && num !== 0) return "";

        const numValue = typeof num === "string" ? parseFloat(num) : num;
        if (isNaN(numValue)) return "";

        return numValue.toLocaleString("de-DE", {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces,
        });
    }, [decimalPlaces]);

    const displayValue = useMemo(() => {
        if (focused) {
            return editingValue;
        } else {
            return formatQuantity(value);
        }
    }, [focused, editingValue, value, formatQuantity]);

    const handleInputChange = useCallback((e) => {
        let inputValue = e.target.value;

        // Prevent negative values
        if (parseFloat(inputValue.replace(',', '.')) < 0) {
            inputValue = "";
        }

        // Store the raw input value (with comma if user typed it)
        setEditingValue(inputValue);

        // For onChange callback, normalize to period for consistency
        let normalizedValue = inputValue;
        if (inputValue.includes(",")) {
            normalizedValue = inputValue.replace(",", ".");
        }

        if (onChange) {
            const modifiedEvent = {
                ...e,
                target: {
                    ...e.target,
                    value: normalizedValue
                }
            };
            onChange(modifiedEvent);
        }
    }, [onChange]);

    const handleFocus = useCallback((e) => {
        setFocused(true);

        if (value) {
            // Convert to editable format - keep the decimal separator that user prefers
            // If the value came from server/props with period, allow user to type comma
            const editableValue = value.toString().replace(",", ".");
            setEditingValue(editableValue);
        } else {
            setEditingValue("");
        }

        onFocus?.(e);
    }, [value, onFocus]);

    const handleBlur = useCallback((e) => {
        setFocused(false);

        const targetValue = e.target.value;
        if (targetValue) {
            // Normalize the value by replacing comma with period before parsing
            const normalizedValue = targetValue.replace(",", ".");
            const numericValue = parseFloat(normalizedValue);

            if (!isNaN(numericValue)) {
                // Update the event target value with normalized value for parent components
                e.target.value = numericValue.toString();
            }
        }

        onBlur?.(e);
    }, [onBlur]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === "-") {
            e.preventDefault();
        }
    }, []);

    return (
        <div className="quantity-input-wrapper">
            {label && (
                <Form.Label htmlFor={fieldId}>
                    {label}
                    {required && <span className="text-danger">*</span>}
                </Form.Label>
            )}

            <Form.Group>
                <InputGroup>
                    <Form.Control
                        id={fieldId}
                        type="text"
                        className={className}
                        placeholder={placeholder}
                        value={displayValue}
                        onChange={handleInputChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        disabled={disabled}
                        required={required}
                        isInvalid={!!error}
                        ref={ref}
                        {...rest}
                    />
                    {suffix && <InputGroup.Text>{suffix}</InputGroup.Text>}

                    {error && (
                        <Form.Control.Feedback type="invalid">
                            {t(error, errorTextPlaceholder)}
                        </Form.Control.Feedback>
                    )}
                </InputGroup>
            </Form.Group>

            {hint && !error && (
                <div className={`${hintClassName} small mt-1 tf-hint`}>
                    {hint}
                </div>
            )}
        </div>
    );
});

QuantityInput.displayName = "QuantityInput";

export default React.memo(QuantityInput);
